import { toApiError } from '@/lib/api/errors'
import {
  OperationVerificationDocumentUploadError,
  requestOperationVerificationUploadUrl,
  uploadOperationVerificationDocument,
} from '@/lib/venue/operationVerificationApi'
import {
  isOperationVerificationImageFile,
  OPERATION_VERIFICATION_DOCUMENT_LIMITS,
  validateOperationVerificationDocument,
  type OperationVerificationDocumentState,
} from '@/lib/venue/operationVerificationViewModel'

/**
 * 인증 서류 첨부 단일 필드(요구사항 1, design.md §2 아키텍처 개요 2).
 *
 * 🚨 `VenueImageListEditor` 와 같은 상태 패턴(uploading/done/error)이지만 단일 파일이라 배열이
 * 아닌 단일 상태를 쓴다. 같은 이유로 별도 훅 없이 `operationVerificationApi.ts` 의 EP-2+PUT 함수를
 * 여기서 직접 호출한다(재사용처 1곳, design.md §2 자율 결정).
 * 🚫 재시도 버튼은 두지 않는다 — 실패하면 파일을 다시 선택해 다시 시도한다(`VenueImageListEditor` 관례).
 *
 * 🚨 `#887` — 이미지 파일(jpg/jpeg/png)은 `VenueMainImageField.tsx`와 같은 톤의 썸네일을 보여준다(PDF는
 * 브라우저 표준 API만으로 미리보기가 불가능해 기존처럼 파일명 텍스트만 유지). `URL.createObjectURL`은
 * 형제 이미지 컴포넌트들과 동일하게 명시적으로 해제하지 않는다(design.md §2 자율 결정 5).
 *
 * 🚨 `#1191` — `required` prop(기본 `false`, 기존 호출부 동작 무변경). 사업자 정보 수정 요청 폼은
 * 서류가 선택이 아니라 필수(백엔드 D4 "전량 교체")라 라벨·안내 문구만 다르게 렌더한다 — 업로드
 * 상태 머신·검증·에러 처리는 완전히 동일해 새 컴포넌트를 만들지 않았다(design.md §2 자율 결정).
 */
export function OperationVerificationDocumentField({
  venueId,
  document: documentState,
  onChange,
  required = false,
}: {
  venueId: string
  document: OperationVerificationDocumentState
  onChange: (document: OperationVerificationDocumentState) => void
  required?: boolean
}) {
  async function handleFileSelected(file: File | null) {
    if (!file) return

    const previewUrl = isOperationVerificationImageFile(file.name) ? URL.createObjectURL(file) : null

    const validationError = validateOperationVerificationDocument(file)
    if (validationError) {
      onChange({
        status: 'error',
        fileName: file.name,
        documentKey: null,
        errorMessage: validationError,
        previewUrl,
        fileSizeBytes: null,
      })
      return
    }

    onChange({
      status: 'uploading',
      fileName: file.name,
      documentKey: null,
      errorMessage: null,
      previewUrl,
      fileSizeBytes: null,
    })

    try {
      const uploadUrlResult = await requestOperationVerificationUploadUrl(venueId, {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      })
      await uploadOperationVerificationDocument(uploadUrlResult.uploadUrl, uploadUrlResult.requiredHeaders, file)
      onChange({
        status: 'done',
        fileName: file.name,
        documentKey: uploadUrlResult.documentKey,
        errorMessage: null,
        previewUrl,
        // 🚨 `#1191` — 신고값을 서버가 다시 HEAD로 재확인하지만(api-spec §D7-M1), 폼 제출 바디에는
        // 이 값을 실어 보내야 한다(`documentSize` 필수 필드).
        fileSizeBytes: file.size,
      })
    } catch (error) {
      const message =
        error instanceof OperationVerificationDocumentUploadError ? error.message : toApiError(error).message
      onChange({
        status: 'error',
        fileName: file.name,
        documentKey: null,
        errorMessage: message,
        previewUrl,
        fileSizeBytes: null,
      })
    }
  }

  function handleRemove() {
    onChange({
      status: 'idle',
      fileName: null,
      documentKey: null,
      errorMessage: null,
      previewUrl: null,
      fileSizeBytes: null,
    })
  }

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="operation-verification-document">
        인증 서류 첨부{' '}
        {required ? null : <span className="text-xs font-normal text-slateish-500">(선택)</span>}
      </label>

      {documentState.previewUrl ? (
        <div className="relative mb-2 aspect-square w-20 overflow-hidden rounded-lg border border-slateish-200 bg-slateish-50">
          <img src={documentState.previewUrl} alt="인증 서류 미리보기" className="h-full w-full object-cover" />

          {documentState.status === 'uploading' ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-[11px] text-white">
              업로드 중…
            </div>
          ) : null}

          {documentState.status === 'error' ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-[11px] text-white">
              실패
            </div>
          ) : null}
        </div>
      ) : null}

      <input
        id="operation-verification-document"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        disabled={documentState.status === 'uploading'}
        className="block text-sm text-ink-700"
        onChange={(event) => {
          void handleFileSelected(event.target.files?.[0] ?? null)
          event.target.value = ''
        }}
      />
      <p className="mt-1 text-xs text-slateish-500">
        {OPERATION_VERIFICATION_DOCUMENT_LIMITS.allowedExtensionsLabel} 파일, 최대 10MB까지 첨부할 수 있습니다.
        {required
          ? ' 사업자등록번호가 바뀌면 사업자등록증도 새로 첨부해야 합니다.'
          : ' 지금 첨부하지 않아도 제출할 수 있어요 — 증빙이 없으면 반려될 수 있습니다.'}
      </p>

      {documentState.status === 'uploading' && !documentState.previewUrl ? (
        <p className="mt-1.5 text-xs text-slateish-500">업로드 중…</p>
      ) : null}

      {documentState.status === 'done' && documentState.fileName ? (
        <div className="mt-1.5 flex items-center gap-2 text-sm text-ink-700">
          <span>{documentState.fileName}</span>
          <button type="button" className="text-xs text-slateish-500 underline" onClick={handleRemove}>
            제거
          </button>
        </div>
      ) : null}

      {documentState.status === 'idle' && documentState.fileName ? (
        <p className="mt-1.5 text-xs text-slateish-500">
          직전 첨부: {documentState.fileName} (서류는 재사용되지 않습니다 — 유지하려면 다시 첨부해 주세요)
        </p>
      ) : null}

      {documentState.status === 'error' ? (
        <p className="mt-1.5 text-xs text-danger-500">{documentState.errorMessage}</p>
      ) : null}
    </div>
  )
}
